// CPauseDialog — 牌局內奶油插畫暫停／設定選單
//
// === 使用圖片 ===
// 面板與按鈕透過 dialog-skin 使用奶油粗框與 2D 按鈕，素材詳見該檔檔頭。
// 設定開關與分段選項由 CSettingsControl 呈現，詳見 settings-control 檔頭。
// 遮罩與文字使用 Rectangle、Text。

import type Phaser from 'phaser';
import { PAGE_THEME } from './page-skin';
import { playUIClick } from '@audio/scene-audio';

import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import { ZH_TW } from '@i18n/zh-tw';
import {
  CSpatialKeyboardNavigator,
  KeyboardNavigationResult,
  type IKeyboardFocusTarget,
} from '@input/spatial-keyboard-navigator';
import type { TSettings } from '@storage/index';

import { showDialog, hideDialog } from './motion';
import { createDialogPanel, createDialogButtonSurface } from './dialog-skin';
import {
  CSettingsControl,
  SettingsControlKind,
  type ISettingsControlConfig,
} from './settings-control';
import { FONT_STYLES } from './font-styles';
import { UIDepth } from './ui-depths';

const SHADE_COLOR = 0x000000; // 全畫面遮罩色
const SHADE_ALPHA = 0.72; // 全畫面遮罩透明度
const PANEL_WIDTH = 610; // 對話框寬
const PANEL_HEIGHT = 520; // 對話框高
const TITLE_Y = -205; // 標題相對面板中心 Y
const BUTTON_WIDTH = 330; // 主選單按鈕寬
const BUTTON_HEIGHT = 66; // 主選單按鈕高
const MAIN_BUTTON_START_Y = -95; // 主選單第一顆按鈕 Y
const MAIN_BUTTON_GAP = 92; // 主選單按鈕中心距
const SETTINGS_ROW_LABEL_X = -220; // 設定列標題 X
const SETTINGS_TOGGLE_X = 150; // 音效／BGM 開關 X
const SETTINGS_AUDIO_Y = -105; // 音效列 Y
const SETTINGS_BGM_Y = -25; // BGM 列 Y
const SETTINGS_TIMER_LABEL_Y = 62; // 計時設定標題 Y
const SETTINGS_TIMER_BUTTON_Y = 125; // 計時選項按鈕 Y
const SETTINGS_TIMER_BUTTON_WIDTH = 112; // 計時選項按鈕寬
const SETTINGS_TIMER_BUTTON_HEIGHT = 46; // 計時選項按鈕高
const SETTINGS_TIMER_BUTTON_GAP = 114; // 計時選項中心距
const SETTINGS_BACK_Y = 210; // 返回主選單按鈕 Y
const TOGGLE_BUTTON_WIDTH = 104; // 開關按鈕寬
const TOGGLE_BUTTON_HEIGHT = 40; // 開關按鈕高
const DIALOG_DEPTH = UIDepth.PauseDialog; // 高於牌局所有互動與儀式

export interface IPauseDialogConfig {
  readonly settings: TSettings;
  readonly onContinue: () => void;
  readonly onBackHome: () => void;
  readonly onSettingsChanged: (patch: Partial<TSettings>) => TSettings;
}

export class CPauseDialog {
  private readonly root: Phaser.GameObjects.Container;
  private content: Phaser.GameObjects.Container;
  private settings: TSettings;
  private readonly keyboardNavigator = new CSpatialKeyboardNavigator();
  private keyboardTargets: IKeyboardFocusTarget[] = [];
  private settingsOpen = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly config: IPauseDialogConfig
  ) {
    this.settings = config.settings;
    const shade = scene.add
      .rectangle(
        LOGICAL_WIDTH / 2,
        LOGICAL_HEIGHT / 2,
        LOGICAL_WIDTH,
        LOGICAL_HEIGHT,
        SHADE_COLOR,
        SHADE_ALPHA
      )
      .setInteractive();
    const panel = createDialogPanel(scene, 0, 0, PANEL_WIDTH, PANEL_HEIGHT);
    this.content = scene.add.container(0, 0);
    const panelRoot = scene.add.container(LOGICAL_WIDTH / 2, LOGICAL_HEIGHT / 2, [
      panel,
      this.content,
    ]);
    this.root = scene.add
      .container(0, 0, [shade, panelRoot])
      .setDepth(DIALOG_DEPTH)
      .setVisible(false);
    this.buildMain();
  }

  show(): void {
    this.buildMain();
    showDialog(this.scene, this.root, this.root.list[0]!, LOGICAL_WIDTH / 2, LOGICAL_HEIGHT / 2);
  }

  hide(): void {
    hideDialog(this.scene, this.root);
    this.keyboardNavigator.clear();
  }

  isVisible(): boolean {
    return this.root.getData('motion-open') === true;
  }

  destroy(): void {
    this.keyboardNavigator.clear();
    this.root.destroy(true);
  }

  /** 暫停選單的方向鍵、確認鍵與返回鍵。回傳 true 表示事件已處理。 */
  handleKeyboard(event: KeyboardEvent): boolean {
    if (!this.isVisible()) return false;
    if (event.key === 'Escape' || event.key === 'Backspace') {
      if (this.settingsOpen) this.buildMain();
      else this.config.onContinue();
      return true;
    }
    return this.keyboardNavigator.handleKey(event.key) !== KeyboardNavigationResult.Ignored;
  }

  private resetContent(): void {
    this.keyboardNavigator.clear();
    this.keyboardTargets = [];
    this.content.removeAll(true);
  }

  private buildMain(): void {
    this.settingsOpen = false;
    this.resetContent();
    const title = this.createTitle(ZH_TW.game.pause.title);
    const continueButton = this.createButton(
      0,
      MAIN_BUTTON_START_Y,
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
      ZH_TW.game.pause.continue,
      false,
      this.config.onContinue
    );
    const settingsButton = this.createButton(
      0,
      MAIN_BUTTON_START_Y + MAIN_BUTTON_GAP,
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
      ZH_TW.game.pause.settings,
      false,
      () => this.buildSettings()
    );
    const homeButton = this.createButton(
      0,
      MAIN_BUTTON_START_Y + MAIN_BUTTON_GAP * 2,
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
      ZH_TW.game.pause.backHome,
      false,
      this.config.onBackHome
    );
    this.content.add([title, continueButton, settingsButton, homeButton]);
    this.keyboardNavigator.setTargets(this.keyboardTargets);
  }

  private buildSettings(): void {
    this.settingsOpen = true;
    this.resetContent();
    const title = this.createTitle(ZH_TW.game.pause.settingsTitle);
    const audioLabel = this.createRowLabel(SETTINGS_AUDIO_Y, ZH_TW.settings.audio);
    const bgmLabel = this.createRowLabel(SETTINGS_BGM_Y, ZH_TW.settings.bgm);
    const timerLabel = this.scene.add
      .text(0, SETTINGS_TIMER_LABEL_Y, ZH_TW.settings.discardTimeout, {
        ...FONT_STYLES.body,
        fontFamily: PAGE_THEME.font,
        color: '#ffe9a8',
        fontSize: '24px',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const audioToggle = this.createSettingControl({
      x: SETTINGS_TOGGLE_X,
      y: SETTINGS_AUDIO_Y,
      width: TOGGLE_BUTTON_WIDTH,
      height: TOGGLE_BUTTON_HEIGHT,
      kind: SettingsControlKind.Switch,
      selected: this.settings.audioOn,
      onActivate: () => this.updateSetting({ audioOn: !this.settings.audioOn }),
    });
    const bgmToggle = this.createSettingControl({
      x: SETTINGS_TOGGLE_X,
      y: SETTINGS_BGM_Y,
      width: TOGGLE_BUTTON_WIDTH,
      height: TOGGLE_BUTTON_HEIGHT,
      kind: SettingsControlKind.Switch,
      selected: this.settings.bgmOn,
      onActivate: () => this.updateSetting({ bgmOn: !this.settings.bgmOn }),
    });

    const timeoutOptions: ReadonlyArray<{
      readonly value: TSettings['discardTimeout'];
      readonly label: string;
    }> = [
      { value: 8, label: ZH_TW.settings.discardTimeoutOptions[8] },
      { value: 15, label: ZH_TW.settings.discardTimeoutOptions[15] },
      { value: 30, label: ZH_TW.settings.discardTimeoutOptions[30] },
      { value: null, label: ZH_TW.game.pause.timerOff },
    ];
    const timerButtons = timeoutOptions.map((option, index) =>
      this.createSettingControl({
        x: (index - (timeoutOptions.length - 1) / 2) * SETTINGS_TIMER_BUTTON_GAP,
        y: SETTINGS_TIMER_BUTTON_Y,
        width: SETTINGS_TIMER_BUTTON_WIDTH,
        height: SETTINGS_TIMER_BUTTON_HEIGHT,
        kind: SettingsControlKind.Segment,
        label: option.label,
        selected: this.settings.discardTimeout === option.value,
        onActivate: () => this.updateSetting({ discardTimeout: option.value }),
      })
    );
    const backButton = this.createButton(
      0,
      SETTINGS_BACK_Y,
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
      ZH_TW.game.pause.back,
      false,
      () => this.buildMain()
    );
    this.content.add([
      title,
      audioLabel,
      bgmLabel,
      timerLabel,
      audioToggle,
      bgmToggle,
      ...timerButtons,
      backButton,
    ]);
    this.keyboardNavigator.setTargets(this.keyboardTargets);
  }

  private updateSetting(patch: Partial<TSettings>): void {
    const focusIndex = this.keyboardNavigator.getFocusIndex();
    this.settings = this.config.onSettingsChanged(patch);
    this.buildSettings();
    this.keyboardNavigator.focus(focusIndex);
  }

  private createSettingControl(config: ISettingsControlConfig): CSettingsControl {
    const control = new CSettingsControl(this.scene, config);
    this.keyboardTargets.push({
      x: config.x,
      y: config.y,
      activate: () => control.activate(),
      setFocused: (focused) => control.setFocused(focused),
    });
    return control;
  }

  private createTitle(label: string): Phaser.GameObjects.Text {
    return this.scene.add
      .text(0, TITLE_Y, label, {
        ...FONT_STYLES.title,
        fontFamily: PAGE_THEME.font,
        color: PAGE_THEME.text,
        strokeThickness: 0,
        fontSize: '38px',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private createRowLabel(y: number, label: string): Phaser.GameObjects.Text {
    return this.scene.add
      .text(SETTINGS_ROW_LABEL_X, y, label, {
        ...FONT_STYLES.body,
        fontFamily: PAGE_THEME.font,
        color: PAGE_THEME.text,
        strokeThickness: 0,
        fontSize: '25px',
      })
      .setOrigin(0, 0.5);
  }

  private createButton(
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    active: boolean,
    onActivate: () => void
  ): Phaser.GameObjects.Container {
    const graphics = createDialogButtonSurface(this.scene, 0, 0, width, height, active);
    graphics.setPrimary(label === ZH_TW.game.pause.continue);
    const text = this.scene.add
      .text(0, 0, label, {
        ...FONT_STYLES.body,
        fontFamily: PAGE_THEME.font,
        color: PAGE_THEME.text,
        strokeThickness: 0,
        fontSize: '23px',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const hitArea = this.scene.add
      .zone(0, 0, width, height)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        playUIClick(this.scene);
        onActivate();
      });
    graphics.bindInteraction(hitArea);
    const button = this.scene.add.container(x, y, [graphics, text, hitArea]);
    this.keyboardTargets.push({
      x,
      y,
      activate: () => {
        playUIClick(this.scene);
        onActivate();
      },
      setFocused: (focused) => graphics.setFocused(focused),
    });
    return button;
  }
}
